'use client';

/**
 * The web call's close (Wave C) — the moment the orb stops being ephemeral.
 *
 * Polls the participant-token-gated lead read for ~10s ("Tessa is writing up
 * what you talked about…"); a draft turns into the claim CTA into the
 * prefilled wizard. No draft after the window → POST ensures one
 * deterministically (the close NEVER dead-ends) — and if even that fails,
 * the generic wizard link stands. Email is optional; the wizard's own gate
 * is the account creator (never Pattern A from the modal).
 */

import { useEffect, useRef, useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';

const POLL_MS = 2000;
const MAX_POLLS = 5; // ~10s

interface CloseDraft {
  slug: string;
  token: string;
  name: string | null;
}

export function PostCallCloseCard({ participantToken }: { participantToken: string }) {
  const [draft, setDraft] = useState<CloseDraft | null>(null);
  const [phase, setPhase] = useState<'writing' | 'ready' | 'fallback'>('writing');
  const polls = useRef(0);

  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch('/api/public/onboarding-lead', {
          headers: { Authorization: `Bearer ${participantToken}` },
          cache: 'no-store',
        });
        if (res.ok) {
          const data = (await res.json()) as { draft?: CloseDraft | null };
          if (!cancelled && data.draft) {
            setDraft(data.draft);
            setPhase('ready');
            return;
          }
        }
      } catch {
        // keep polling
      }
      polls.current += 1;
      if (polls.current >= MAX_POLLS) {
        // Ensure deterministically — the close never dead-ends.
        try {
          const res = await fetch('/api/public/onboarding-lead', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${participantToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({}),
          });
          if (res.ok) {
            const data = (await res.json()) as { draft?: CloseDraft | null };
            if (!cancelled && data.draft) {
              setDraft(data.draft);
              setPhase('ready');
              return;
            }
          }
        } catch {
          // fall through
        }
        if (!cancelled) setPhase('fallback');
        return;
      }
      timer = setTimeout(poll, POLL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [participantToken]);

  if (phase === 'writing') {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-12 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground">
          Tessa is writing up what you talked about…
        </p>
      </div>
    );
  }

  if (phase === 'ready' && draft) {
    return (
      <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
        <CheckCircle2 className="h-8 w-8 text-[#10B981]" />
        <div>
          <h3 className="text-lg font-semibold">
            {draft.name ? `"${draft.name}" is drafted` : 'Your check-in is drafted'}
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Built from your conversation — review it, tweak anything, launch when ready.
          </p>
        </div>
        <Button asChild className="rounded-full bg-[#EF4444] px-7 hover:bg-[#DC2626]">
          <a href={`/routines/new/${draft.slug}?draft=${encodeURIComponent(draft.token)}&campaign=voice-close`}>
            See your draft →
          </a>
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 px-6 py-10 text-center">
      <h3 className="text-lg font-semibold">Thanks for talking with Tessa</h3>
      <p className="text-sm text-muted-foreground">
        Set up your own team check-in in about two minutes — free.
      </p>
      <Button asChild className="rounded-full bg-[#EF4444] px-7 hover:bg-[#DC2626]">
        <a href="/routines/new?campaign=voice-demo">Start your check-in →</a>
      </Button>
    </div>
  );
}
